import React, { useState, useEffect } from "react";
import { VerticalTimeline, VerticalTimelineElement } from "react-vertical-timeline-component";
import { getFullName } from "utils/displayName";
import moment from "moment";
import JSONPretty from "react-json-pretty";
import { Modal, Grid, Icon, Button, Loader } from "semantic-ui-react";
import "react-vertical-timeline-component/style.min.css";
import "styles/timeline.css";

const LogTimeline = ({ logs }) => {
	const [clickedLog, setClickedLog] = useState(null);
	//{action: 'CREATE', entity:'Tube'}{action: 'CREATE', entity:'QuestionAnswer'},{action: 'CREATE', entity:'PatientExternalIds'},
	const notPertinentLogsEntities = [
		{ action: "CREATE", entity: "QuestionAnswer" },
		{ action: "CREATE", entity: "Tube" },
		{ action: "UPDATE", entity: "LabAppointment" },
		{ action: "UPDATE", entity: "Analysis" },
		{ action: "CREATE", entity: "PatientExternalIds" },
		{ action: "CREATE", entity: "Costs" },
		{ action: "UPDATE", entity: "Costs" },
		{ action: "CREATE", entity: "MySecuCheck" },
		{ action: "UPDATE", entity: "MySecuCheck" },
	];

	return (
		<div>
			{logs.list.length > 0 && (
				<VerticalTimeline layout="1-column" className="vertical-timeline-custom-line">
					{logs.list
						.filter((l) => !notPertinentLogsEntities.some((f) => l.action === f.action && l.entity === f.entity))
						.map((log) => (
							<TimeLineElement log={log} key={`tle-${log.id}`} openJSONVisualizationModal={(log) => setClickedLog(log)} logs={logs.list} />
						))}
					<VerticalTimelineElement iconStyle={{ background: "#008000", color: "#fff" }} icon={<Icon name="play" size="large" />} />
				</VerticalTimeline>
			)}

			{logs.list.length === 0 && !logs.loading && (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
					}}
				>
					<span>Aucun log</span>
				</div>
			)}

			{logs.loading && (
				<div style={{ margin: "20px 0" }}>
					<Loader active />
				</div>
			)}

			{clickedLog && <JSONVisualizationModal log={clickedLog} open={true} close={() => setClickedLog(null)} />}
		</div>
	);
};

const TimeLineElement = ({ log, openJSONVisualizationModal, logs }) => {
	const [backgroundColor, setBackgroundColor] = useState("#EEEEEE");

	useEffect(() => {
		switch (log.action) {
			case "CREATE":
				setBackgroundColor("#008000");
				break;
			case "UPDATE":
				setBackgroundColor("#FF8C00");
				break;
			case "DELETE":
				setBackgroundColor("#8B0000");
				break;
			default:
				setBackgroundColor("#eeeeee");
				break;
		}
	}, [log]);

	const getIcon = () => {
		switch (log.entity) {
			case "Folder":
				return <Icon name="folder" size="large" />;
			case "Prescription":
				return <Icon name="file alternate" size="large" />;
			case "QuestionAnswer":
				return <Icon name="rocketchat" size="large" />;
			case "Tube":
				return <Icon name="flask" size="large" />;
			case "payment":
				return <Icon name="euro sign" size="large" />;
			case "Patient":
				return <Icon name="street view" size="large" />;
			case "Analysis":
				return <Icon name="clipboard list" size="large" />;
			case "Appointment":
			case "LabAppointment":
				return <Icon name="calendar alternate outline" size="large" />;
			default:
				return <Icon name="computer" size="large" />;
		}
	};

	return (
		<VerticalTimelineElement
			onTimelineElementClick={() => {
				openJSONVisualizationModal(log);
			}}
			contentStyle={{ background: backgroundColor, color: "#fff" }}
			contentArrowStyle={{ borderRight: `7px solid ${backgroundColor}` }}
			date={moment(log.created_at).format(`${process.env.REACT_APP_DATE_DISPLAY_FORMAT}, HH:mm:ss`)}
			iconStyle={{ background: backgroundColor, color: "#fff" }}
			icon={getIcon()}
		>
			{getFirstInfos(log, logs)}
		</VerticalTimelineElement>
	);
};

const JSONVisualizationModal = ({ log, open, close }) => {
	const isUpdate = log.action === "UPDATE";
	return (
		<Modal open={open} onClose={close} closeIcon>
			<Modal.Header>
				{log.entity} N° {log.entity_id} - {getAction(log)} -
				{moment(log.created_at).format(`${process.env.REACT_APP_DATE_DISPLAY_FORMAT}, ${process.env.REACT_APP_TIME_DISPLAY_FORMAT}`)}
			</Modal.Header>
			<Modal.Content>
				<Grid columns={isUpdate ? 3 : 1}>
					{log.action !== "CREATE" && (
						<Grid.Column computer={isUpdate ? 7 : 16} tablet={isUpdate ? 7 : 16} style={styles.verticalCenter}>
							<JSONPretty data={log.old_value} />
						</Grid.Column>
					)}
					{isUpdate && (
						<Grid.Column computer={2} tablet={2} style={styles.center}>
							<Icon name="arrow right" />
						</Grid.Column>
					)}
					{log.action !== "DELETE" && (
						<Grid.Column computer={isUpdate ? 7 : 16} tablet={isUpdate ? 7 : 16} style={styles.verticalCenter}>
							<JSONPretty data={log.new_value} />
						</Grid.Column>
					)}
				</Grid>
			</Modal.Content>
			<Modal.Actions>
				<Button content="Fermer" onClick={close} />
			</Modal.Actions>
		</Modal>
	);
};

const getAction = (log) => {
	let action;
	switch (log.action) {
		case "CREATE":
			action = "Création";
			break;
		case "UPDATE":
			action = "Modification";
			break;
		case "DELETE":
			action = "Suppression";
			break;
		default:
			action = "";
			break;
	}

	return `${action} ${log.check ? `par ${log.user}, utilisateur N°${log.user_id}` : ""}`;
};

const getEntity = (log, allLogs) => {
	const getCreationInfos = (entity, entity_id) => {
		return allLogs ? allLogs.filter((l) => l.entity_id === entity_id && l.entity === entity && l.action === "CREATE") : [];
	};
	if (log.entity === "FolderHelp") {
		let libelle = "Dossier du télésecrétariat";
		if (log.new_value && JSON.parse(log.new_value) && JSON.parse(log.new_value).comments && JSON.parse(log.new_value).comments.length > 0) {
			libelle += ` -  ${JSON.parse(log.new_value).comments[0].comment}`;
		}
		return libelle;
	}

	if (log.entity === "Folder") {
		let libelle = "Dossier";
		if (log.new_value && JSON.parse(log.new_value) && JSON.parse(log.new_value).code) {
			libelle += ` ${JSON.parse(log.new_value).code}`;
		}
		if (log.new_value && JSON.parse(log.new_value) && JSON.parse(log.new_value).json_generated !== undefined)
			libelle += JSON.parse(log.new_value).json_generated === true ? " envoyé à GesLab" : "";
		if (log.new_value && JSON.parse(log.new_value) && JSON.parse(log.new_value).openedBy !== undefined)
			libelle += JSON.parse(log.new_value).openedBy === null ? " fermé" : " ouvert";
		if (log.new_value && JSON.parse(log.new_value) && JSON.parse(log.new_value).status !== undefined)
			libelle += JSON.parse(log.new_value).status === "DONE" ? " validé" : "";
		return libelle;
	}
	if (log.entity === "Tube") {
		let libelle = "Tube";
		if (log.new_value && JSON.parse(log.new_value) && JSON.parse(log.new_value).analysis && JSON.parse(log.new_value).analysis.id) {
			const creationLogs = getCreationInfos("Analysis", JSON.parse(log.new_value).analysis.id);
			if (
				creationLogs.length > 0 &&
				creationLogs[0].new_value &&
				JSON.parse(creationLogs[0].new_value) &&
				JSON.parse(creationLogs[0].new_value).properties &&
				JSON.parse(creationLogs[0].new_value).properties.code
			) {
				libelle += ` de l'analyse ${JSON.parse(creationLogs[0].new_value).properties.code}`;
			}
		} else {
			const creationTubes = getCreationInfos(log.entity, log.entity_id);
			if (creationTubes.length > 0) {
				if (
					creationTubes[0].new_value &&
					JSON.parse(creationTubes[0].new_value) &&
					JSON.parse(creationTubes[0].new_value).analysis &&
					JSON.parse(creationTubes[0].new_value).analysis.id
				) {
					const creationLogs = getCreationInfos("Analysis", JSON.parse(creationTubes[0].new_value).analysis.id);
					if (
						creationLogs.length > 0 &&
						creationLogs[0].new_value &&
						JSON.parse(creationLogs[0].new_value) &&
						JSON.parse(creationLogs[0].new_value).properties &&
						JSON.parse(creationLogs[0].new_value).properties.code
					) {
						libelle += ` de l'analyse ${JSON.parse(creationLogs[0].new_value).properties.code}`;
					}
				}
			}
		}

		return libelle;
	}
	if (log.entity === "deposit") return "Dépôt";
	if (log.entity === "Costs") return "Reste à charge";
	if (log.entity === "payment") return `Paiement`;
	if (log.entity === "LabAppointment") return `Prise en charge`;
	if (log.entity === "PatientInsurance") return `Mutuelle`;
	if (log.entity === "endo_answers") return "Réponse onglet ENDOTEST";
	if (log.entity === "Analysis") {
		if (log.new_value && JSON.parse(log.new_value) && JSON.parse(log.new_value).properties && JSON.parse(log.new_value).properties.code) {
			return `Analyse ${JSON.parse(log.new_value).properties.code}`;
		}
		const creationLogs = getCreationInfos(log.entity, log.entity_id);
		if (
			creationLogs.length > 0 &&
			creationLogs[0].new_value &&
			JSON.parse(creationLogs[0].new_value) &&
			JSON.parse(creationLogs[0].new_value).properties &&
			JSON.parse(creationLogs[0].new_value).properties.code
		)
			return `Analyse ${JSON.parse(creationLogs[0].new_value).properties.code}`;
	}
	if (log.entity === "QuestionAnswer") return `Réponse au questionnaire`;
	if (log.entity === "record")
		return `Réponse à la question : ${
			log.new_value && JSON.parse(log.new_value) && JSON.parse(log.new_value).question ? JSON.parse(log.new_value).question.label : ""
		}`;
	return log.entity;
};

const getFirstInfos = (log, allLogs) => {
	const displayPertinentInfo = () => {
		if (log.new_value) {
			if (log.entity === "FolderHelp")
				return (
					<div>
						{JSON.parse(log.new_value).fromSafeplace ? <div>Safeplace avec ordonnance</div> : ""}
						{JSON.parse(log.new_value).status === "ASSIGNED" ? (
							<div>
								Assigné automatiquement à {JSON.parse(log.new_value).assigned_to.first_name} {JSON.parse(log.new_value).assigned_to.last_name}
							</div>
						) : (
							""
						)}
						{JSON.parse(log.new_value).status === "SUPPORT" ? <div>Prise en charge</div> : ""}
						{JSON.parse(log.new_value).status === "REASSIGNED" ? <div>Dossier réassigné à l'infirmière</div> : ""}
						{JSON.parse(log.new_value).status === "DONE" ? <div>Télésecrétariat terminé</div> : ""}
					</div>
				);
			if (log.entity === "LabAppointment")
				return (
					<div>
						{JSON.parse(log.new_value).box ? (
							<div>
								Centre : {JSON.parse(log.new_value).box.center?.name} (Box: {JSON.parse(log.new_value).box.name})
							</div>
						) : JSON.parse(log.new_value).center ? (
							<div>Centre : {JSON.parse(log.new_value).center}</div>
						) : (
							""
						)}
					</div>
				);
			if (log.entity === "Folder")
				return (
					<div>
						{JSON.parse(log.new_value).patient ? (
							<div>
								Patient : {getFullName(JSON.parse(log.new_value).patient)} ({JSON.parse(log.new_value).patient.cns})
							</div>
						) : (
							""
						)}
					</div>
				);
			if (log.entity === "Prescription")
				return (
					<div>
						{JSON.parse(log.new_value).safeplace_code ? <div>SAFEPLACE</div> : ""}
						{JSON.parse(log.new_value).no_prescriber ? <div>Sans prescription</div> : ""}
						{JSON.parse(log.new_value).safeplace_code ? <div>QR code : {JSON.parse(log.new_value).safeplace_code}</div> : ""}
						{JSON.parse(log.new_value).prescriber && JSON.parse(log.new_value).prescriber.id ? (
							<div>
								Prescripteur : {JSON.parse(log.new_value).prescriber.title} {JSON.parse(log.new_value).prescriber.last_name}{" "}
								{JSON.parse(log.new_value).prescriber.first_name}
							</div>
						) : (
							""
						)}
						{JSON.parse(log.new_value).prescriber && JSON.parse(log.new_value).prescriber.id && JSON.parse(log.new_value).issue_date ? (
							<div>Délivrance : {JSON.parse(log.new_value).issue_date}</div>
						) : (
							""
						)}
						{JSON.parse(log.new_value).billing_company && JSON.parse(log.new_value).billing_company.name ? (
							<div>Société de facturation : {JSON.parse(log.new_value).billing_company.name}</div>
						) : (
							""
						)}
						{JSON.parse(log.new_value).safeplace_result ? (
							<div>
								Résultat du test :{" "}
								{JSON.parse(log.new_value).safeplace_result === "p"
									? "positif"
									: JSON.parse(log.new_value).safeplace_result === "n"
									? "négatif"
									: "invalide"}
							</div>
						) : (
							""
						)}
					</div>
				);
			if (log.entity === "payment")
				return (
					<div>{`Montant : ${JSON.parse(log.new_value).price}€ - Moyen de paiement : ${JSON.parse(log.new_value).source}  ${
						JSON.parse(log.new_value).transaction_id ? `(id transaction : ${JSON.parse(log.new_value).transaction_id}` : ""
					})`}</div>
				);

			if (log.entity === "QuestionAnswer") {
				return <div>{JSON.parse(log.new_value).value ? <div> {JSON.parse(log.new_value).value}</div> : ""}</div>;
			}
			if (log.entity === "Tube") {
				return <div>{JSON.parse(log.new_value).provider_barcode ? <div>Code-barres : {JSON.parse(log.new_value).provider_barcode}</div> : ""}</div>;
			}
			if (log.entity === "record") {
				return <div>{JSON.parse(log.new_value).value ? <div>Réponse : {JSON.parse(log.new_value).value}</div> : ""}</div>;
			}
		}

		/*	if (log.entity === 'Folder')
		return <div>{JSON.parse(log.newValue).code}</div>
  switch (log.entity){
			case 'Folder' :  return (<div>{JSON.parse(log.newValue).code}</div>) 
			default: < br/>
		} */
	};
	return (
		<>
			<h3 className="vertical-timeline-element-title">
				{getEntity(log, allLogs)} (N° {log.entity_id})
			</h3>
			<h4 className="vertical-timeline-element-subtitle">{getAction(log)}</h4>
			{displayPertinentInfo(log)}
			{/* {isUpdate && <p>Champs modifiés: {getModifiedFields()}</p>} */}
		</>
	);
};

const styles = {
	center: {
		display: "flex",
		justifyContent: "center",
		alignItems: "center",
	},
	verticalCenter: {
		display: "flex",
		alignItems: "center",
	},
};

export default LogTimeline;
